import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toast } from "sonner";
import { deleteTopic, getTopics } from "../../api/topic.service";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const Topics = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const navigation = [t("topics.tabsAll"), t("topics.tabsActive"), t("topics.tabsDraft")];
  const [activeTab, setActiveTab] = useState(0);
  const [topicsData, setTopicsData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isVisibleModalDelete, setisVisibleModalDelete] = useState(false);

  const getTranslation = (translations, lang = "en") => {
    if (!translations || !Array.isArray(translations)) return "Untitled";
    const translation = translations.find((t) => t?.lang === lang);
    return translation?.trans || translations[0]?.trans || "Untitled";
  };

  const handleGetTopics = useCallback(async () => {
    try {
      let response = await getTopics();
      if (response?.data?.results) {
        setTopicsData(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error(t("toast.errorLoadingTopics"));
    }
  }, [t]);

  useEffect(() => {
    handleGetTopics();
  }, [handleGetTopics]);

  const handleDelete = async () => {
    try {
      await deleteTopic(selectedItem);
      toast.success(t("toast.topicDeleted"));
      setisVisibleModalDelete(false);
      setSelectedItem(null);
      handleGetTopics();
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error(t("toast.errorDeletingTopic"));
    }
  };

  const getFilteredData = () => {
    const tabLabel = navigation[activeTab];
    if (tabLabel === t("topics.tabsActive")) return topicsData.filter((item) => item?.active === true);
    if (tabLabel === t("topics.tabsDraft")) return topicsData.filter((item) => item?.active === false);
    return topicsData;
  };

  const filteredData = getFilteredData();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>{t("pages.topics")}</Typography>
            <Chip label={filteredData.length} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
          </Stack>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push("/topic/add")}>
            {t("topics.newTopic")}
          </Button>
        </Box>

        <Box sx={{ px: 3, flexShrink: 0 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1 }}>
            {navigation.map((label, i) => (
              <Tab key={i} label={label} sx={{ textTransform: "none", fontWeight: 600 }} />
            ))}
          </Tabs>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("common.title")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("topics.lastUpdate")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("common.status")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("common.detail")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("topics.period")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }} align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((topic, index) => (
                <TableRow key={topic?._id || index} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{getTranslation(topic?.title)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{dayjs(topic?.lastUpdate).format("DD MMM YYYY")}</Typography>
                    <Typography variant="caption" color="text.secondary">{dayjs(topic?.lastUpdate).format("HH:mm[h]")}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={topic?.active ? t("topics.tabsActive") : t("topics.draft")}
                      size="small"
                      color={topic?.active ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{getTranslation(topic?.description)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("topics.decksAndTexts", { decks: topic?.decks?.length || 0, texts: topic?.texts?.length || 0 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {topic?.startDate && (
                      <Box>
                        <Typography variant="caption">{t("topics.start")}: {dayjs(topic.startDate).format("DD/MM/YYYY")}</Typography>
                        {topic?.endDate && (
                          <Typography variant="caption" display="block">{t("topics.end")}: {dayjs(topic.endDate).format("DD/MM/YYYY")}</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" color="primary" onClick={() => history.push(`/topics/edit/${topic?._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => { setSelectedItem(topic); setisVisibleModalDelete(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVisibleModalDelete} onClose={() => { setSelectedItem(null); setisVisibleModalDelete(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("topics.deleteTopic")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("topics.confirmDeleteTopic", { title: getTranslation(selectedItem?.title) })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSelectedItem(null); setisVisibleModalDelete(false); }}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Topics;
